import React, {useEffect, useRef} from "react";
import {css} from "@emotion/react";
import styled from "@emotion/styled";

import {calculateRemSize, flex, flexAbsoluteCenter, p} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader";

import {useGetReelDetailsQuery} from "../../api/get_reel_details";
import {REEL_DEFAULT_COVER_RATIO} from "../../constants/reel_default_cover_ratio";
import {useReelMediaHeightSync} from "../../hooks/use_reel_media_height_sync";
import {useReelPlayer} from "../../hooks/use_reel_player";
import {useReelPlayerProgressAnalytics} from "../../hooks/use_reel_player_progress_analytics";
import {reelsAnalytics} from "../../tracking/reels_gtm_events";
import {IReelDetails} from "../../types/IReelDetails";
import {IReelListItem} from "../../types/IReelListItem";
import {APLA_GRADIENT_COLORS, ReelDetailsApla} from "./reel_details_parts/ReelDetailsApla";
import {ReelDetailsCenterFeedback} from "./reel_details_parts/ReelDetailsCenterFeedback";
import {ReelDetailsCloseButton} from "./reel_details_parts/ReelDetailsCloseButton";
import {ReelDetailsCtaButton} from "./reel_details_parts/ReelDetailsCtaButton";
import {ReelDetailsHeader} from "./reel_details_parts/ReelDetailsHeader";
import {ReelDetailsProgressBar} from "./reel_details_parts/ReelDetailsProgressBar";
import {ReelDetailsShareButton} from "./reel_details_parts/ReelDetailsShareButton";
import {ReelDetailsVideo} from "./reel_details_parts/ReelDetailsVideo";
import {ReelDetailsVideoControls} from "./reel_details_parts/ReelDetailsVideoControls";

interface IProps {
    isActive: boolean;
    reelId: number;
    reelListIndex: number;
    reelDetails: IReelListItem | IReelDetails;
    shareUrl?: string;
    disableFetch?: boolean;
    onVideoEnded?: (reelId: number) => void;
    showCloseBtn?: boolean;
    onClose?: () => void;
    onReady?: (reelData: IReelDetails) => void;
    handleDisableReelsNavigation: () => void;
    handleEnableReelsNavigation: () => void;
    className?: string;
}

export const ReelDetailsFullscreen = (props: IProps) => {
    const {data: reelData} = useGetReelDetailsQuery({id: props.reelId || 0}, {skip: !props.reelId || !!props.disableFetch});
    const wrapperRef = useRef<HTMLDivElement>(null);
    const player = useReelPlayer({onEnded: () => props.onVideoEnded?.(props.reelId)});
    const shareUrl = props.shareUrl;
    const creator = reelData?.creator || props.reelDetails.creator;
    const title = reelData?.title || props.reelDetails.title;
    const reelDetails = reelData || (props.reelDetails as IReelDetails);
    const gtmPayload = {reels_developer: creator.name, reels_name: title, reels_id: props.reelId};

    useEffect(() => {
        if (reelData) {
            props.onReady?.(reelData);
        }
    }, [props.isActive, reelData]);

    useReelPlayerProgressAnalytics({
        reelListIndex: props.reelListIndex + 1,
        videoRef: player.videoRef,
        isReady: props.isActive && !!reelData,
        gtmPayload
    });

    useEffect(() => {
        if (!reelData) {
            return;
        }

        const onKeyPress = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement;

            if (event.code === "Space" && target?.nodeName !== "BUTTON") {
                player.togglePlay();
            }
        };

        if (props.isActive) {
            window.addEventListener("keydown", onKeyPress);
            player.refreshPlayerSettings();
            player.play();
        } else {
            window.removeEventListener("keydown", onKeyPress);
            player.pause();
            player.rewind();
        }

        return () => {
            player.pause();
            window.removeEventListener("keydown", onKeyPress);
        };
    }, [props.isActive, reelData]);

    const onPlay = () => {
        player.play();
    };

    const onPause = () => {
        player.pause();
    };

    const onMute = () => {
        player.mute();
    };

    const onUnmute = () => {
        player.unmute();
    };

    const onActionButtonClick = () => {
        player.pause();
        reelsAnalytics.gtm.reelButtonClickEvent(gtmPayload);
    };

    const onInquiryFormModalOpen = () => {
        player.pause();
        props.handleDisableReelsNavigation();
    };

    const onInquiryFormModalClose = () => {
        props.handleEnableReelsNavigation();
    };

    const {mediaHeight} = useReelMediaHeightSync({videoRef: player.videoRef, wrapperRef, defaultRatio: REEL_DEFAULT_COVER_RATIO});

    return (
        <Wrapper height={mediaHeight} className={props.className}>
            <div ref={wrapperRef} css={mediaWrapperStyle}>
                <div css={videoWrapperStyle}>
                    <ReelDetailsVideo ref={player.videoRef} src={reelDetails?.video} css={[videoStyle]} />
                </div>
                <ReelDetailsApla position="top" />
                <ReelDetailsApla position="bottom" />
            </div>

            <ReelDetailsCenterFeedback show={player.isLoading} delay={500}>
                <Loader size="sm" />
            </ReelDetailsCenterFeedback>

            <Content height={mediaHeight}>
                <div css={controlsWrapperStyle}>
                    <ReelDetailsProgressBar videoRef={player.videoRef} />
                    <ReelDetailsVideoControls
                        isMute={player.isMuted}
                        isPlaying={player.isPlaying}
                        onMute={onMute}
                        onUnmute={onUnmute}
                        onPlay={onPlay}
                        onPause={onPause}
                    />
                    {props.showCloseBtn && <ReelDetailsCloseButton onClick={props.onClose} />}
                </div>

                <ReelDetailsHeader creatorImageSrc={creator.image.r_c_img_48x48} creatorName={creator.name} title={title} />

                <div css={[reelFooterStyle, reelDetails?.action_button_enabled ? flex("flex-end", "space-between") : flex("flex-end", "flex-end")]}>
                    {reelDetails?.action_button_enabled ? (
                        <ReelDetailsCtaButton
                            reel={reelDetails}
                            onInquiryFormModalOpen={onInquiryFormModalOpen}
                            onInquiryFormModalClose={onInquiryFormModalClose}
                            onBtnClick={onActionButtonClick}
                        />
                    ) : null}

                    <div css={actionButtonsWrapperStyle}>
                        {shareUrl ? <ReelDetailsShareButton reelId={props.reelId} title={title} creatorName={creator.name} url={shareUrl} /> : null}
                    </div>
                </div>
            </Content>
        </Wrapper>
    );
};

const Wrapper = styled.div<{backgroundImgSrc?: string; height?: number}>`
    overflow: hidden;
    position: relative;
    background: ${APLA_GRADIENT_COLORS.OUTSIDE};
    width: 100%;
    max-width: 100vw;
    max-height: 100vh;
    max-height: 100dvh;
    transition: height 0.5s ease-in-out;
    ${({backgroundImgSrc}) => (backgroundImgSrc ? `background-image: url("${backgroundImgSrc}");` : "")};
    background-size: cover;

    @media (min-width: ${({theme}) => theme.breakpoints.sm}) {
        width: 36rem;
        height: ${REEL_DEFAULT_COVER_RATIO * 36}rem;
    }

    ${({height}) => height && height > 0 && `height: min(100svh, ${height}px);`}
`;

const Content = styled.div<{height?: number}>`
    position: absolute;
    inset: 0;
    max-height: 100vh;
    max-height: 100dvh;
    z-index: 1;
    height: 100%;
    ${({height}) => height && height > 0 && `height: ${height}px;`}
    width: 100%;
    display: flex;
    flex-direction: column;
`;

const mediaWrapperStyle = css`
    ${flexAbsoluteCenter};
    flex-direction: column;
    position: relative;
    z-index: 1;
`;

const videoWrapperStyle = css`
    position: absolute;
    z-index: 2;
    inset: 0;
`;

const videoStyle = css`
    display: block;
    position: relative;
    z-index: 0;
`;

const controlsWrapperStyle = css`
    ${p(2, 2, 1, 2)};
    ${flex("center", "space-between")};
    gap: ${calculateRemSize(3)};
`;

const reelFooterStyle = css`
    flex: 1 1 auto;
    ${p(3, 2)};
    user-select: none;
    gap: ${calculateRemSize(1.5)};
    width: 100%;
`;

const actionButtonsWrapperStyle = css`
    flex: 0 0 auto;
    ${flex("center", "space-between")};
    flex-direction: row;
    gap: ${calculateRemSize(1)};
`;
import React, {useEffect, useRef} from "react";
import {css} from "@emotion/react";
import styled from "@emotion/styled";

import {calculateRemSize, flex, flexAbsoluteCenter, p} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader";

import {useGetReelDetailsQuery} from "../../api/get_reel_details";
import {REEL_DEFAULT_COVER_RATIO} from "../../constants/reel_default_cover_ratio";
import {useReelMediaHeightSync} from "../../hooks/use_reel_media_height_sync";
import {useReelPlayer} from "../../hooks/use_reel_player";
import {useReelPlayerProgressAnalytics} from "../../hooks/use_reel_player_progress_analytics";
import {reelsAnalytics} from "../../tracking/reels_gtm_events";
import {IReelDetails} from "../../types/IReelDetails";
import {IReelListItem} from "../../types/IReelListItem";
import {APLA_GRADIENT_COLORS, ReelDetailsApla} from "./reel_details_parts/ReelDetailsApla";
import {ReelDetailsCenterFeedback} from "./reel_details_parts/ReelDetailsCenterFeedback";
import {ReelDetailsCloseButton} from "./reel_details_parts/ReelDetailsCloseButton";
import {ReelDetailsCtaButton} from "./reel_details_parts/ReelDetailsCtaButton";
import {ReelDetailsHeader} from "./reel_details_parts/ReelDetailsHeader";
import {ReelDetailsProgressBar} from "./reel_details_parts/ReelDetailsProgressBar";
import {ReelDetailsShareButton} from "./reel_details_parts/ReelDetailsShareButton";
import {ReelDetailsVideo} from "./reel_details_parts/ReelDetailsVideo";
import {ReelDetailsVideoControls} from "./reel_details_parts/ReelDetailsVideoControls";

interface IProps {
    isActive: boolean;
    reelId: number;
    reelListIndex: number;
    reelDetails: IReelListItem | IReelDetails;
    shareUrl?: string;
    disableFetch?: boolean;
    onVideoEnded?: (reelId: number) => void;
    showCloseBtn?: boolean;
    onClose?: () => void;
    onReady?: (reelData: IReelDetails) => void;
    handleDisableReelsNavigation: () => void;
    handleEnableReelsNavigation: () => void;
    className?: string;
}

export const ReelDetailsFullscreen = (props: IProps) => {
    const {data: reelData} = useGetReelDetailsQuery({id: props.reelId || 0}, {skip: !props.reelId || !!props.disableFetch});
    const wrapperRef = useRef<HTMLDivElement>(null);
    const player = useReelPlayer({onEnded: () => props.onVideoEnded?.(props.reelId)});
    const shareUrl = props.shareUrl;
    const creator = reelData?.creator || props.reelDetails.creator;
    const title = reelData?.title || props.reelDetails.title;
    const reelDetails = reelData || (props.reelDetails as IReelDetails);
    const gtmPayload = {reels_developer: creator.name, reels_name: title, reels_id: props.reelId};

    useEffect(() => {
        if (reelData) {
            props.onReady?.(reelData);
        }
    }, [props.isActive, reelData]);

    useReelPlayerProgressAnalytics({
        reelListIndex: props.reelListIndex + 1,
        videoRef: player.videoRef,
        isReady: props.isActive && !!reelData,
        gtmPayload
    });

    useEffect(() => {
        if (!reelData) {
            return;
        }

        const onKeyPress = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement;

            if (event.code === "Space" && target?.nodeName !== "BUTTON") {
                player.togglePlay();
            }
        };

        if (props.isActive) {
            window.addEventListener("keydown", onKeyPress);
            player.refreshPlayerSettings();
            player.play();
        } else {
            window.removeEventListener("keydown", onKeyPress);
            player.pause();
            player.rewind();
        }

        return () => {
            player.pause();
            window.removeEventListener("keydown", onKeyPress);
        };
    }, [props.isActive, reelData]);

    const onPlay = () => {
        player.play();
    };

    const onPause = () => {
        player.pause();
    };

    const onMute = () => {
        player.mute();
    };

    const onUnmute = () => {
        player.unmute();
    };

    const onActionButtonClick = () => {
        player.pause();
        reelsAnalytics.gtm.reelButtonClickEvent(gtmPayload);
    };

    const onInquiryFormModalOpen = () => {
        player.pause();
        props.handleDisableReelsNavigation();
    };

    const onInquiryFormModalClose = () => {
        props.handleEnableReelsNavigation();
    };

    const {mediaHeight} = useReelMediaHeightSync({videoRef: player.videoRef, wrapperRef, defaultRatio: REEL_DEFAULT_COVER_RATIO});

    return (
        <Wrapper height={mediaHeight} className={props.className}>
            <div ref={wrapperRef} css={mediaWrapperStyle}>
                <div css={videoWrapperStyle}>
                    <ReelDetailsVideo ref={player.videoRef} src={reelDetails?.video} css={[videoStyle]} />
                </div>
                <ReelDetailsApla position="top" />
                <ReelDetailsApla position="bottom" />
            </div>

            <ReelDetailsCenterFeedback show={player.isLoading} delay={500}>
                <Loader size="sm" />
            </ReelDetailsCenterFeedback>

            <Content height={mediaHeight}>
                <div css={controlsWrapperStyle}>
                    <ReelDetailsProgressBar videoRef={player.videoRef} />
                    <ReelDetailsVideoControls
                        isMute={player.isMuted}
                        isPlaying={player.isPlaying}
                        onMute={onMute}
                        onUnmute={onUnmute}
                        onPlay={onPlay}
                        onPause={onPause}
                    />
                    {props.showCloseBtn && <ReelDetailsCloseButton onClick={props.onClose} />}
                </div>

                <ReelDetailsHeader creatorImageSrc={creator.image.r_c_img_48x48} creatorName={creator.name} title={title} />

                <div css={[reelFooterStyle, reelDetails?.action_button_enabled ? flex("flex-end", "space-between") : flex("flex-end", "flex-end")]}>
                    {reelDetails?.action_button_enabled ? (
                        <ReelDetailsCtaButton
                            reel={reelDetails}
                            onInquiryFormModalOpen={onInquiryFormModalOpen}
                            onInquiryFormModalClose={onInquiryFormModalClose}
                            onBtnClick={onActionButtonClick}
                        />
                    ) : null}

                    <div css={actionButtonsWrapperStyle}>
                        {shareUrl ? <ReelDetailsShareButton reelId={props.reelId} title={title} creatorName={creator.name} url={shareUrl} /> : null}
                    </div>
                </div>
            </Content>
        </Wrapper>
    );
};

const Wrapper = styled.div<{backgroundImgSrc?: string; height?: number}>`
    overflow: hidden;
    position: relative;
    background: ${APLA_GRADIENT_COLORS.OUTSIDE};
    width: 100%;
    max-width: 100vw;
    max-height: 100vh;
    max-height: 100dvh;
    transition: height 0.5s ease-in-out;
    ${({backgroundImgSrc}) => (backgroundImgSrc ? `background-image: url("${backgroundImgSrc}");` : "")};
    background-size: cover;

    @media (min-width: ${({theme}) => theme.breakpoints.sm}) {
        width: 36rem;
        height: ${REEL_DEFAULT_COVER_RATIO * 36}rem;
    }

    ${({height}) => height && height > 0 && `height: min(100svh, ${height}px);`}
`;

const Content = styled.div<{height?: number}>`
    position: absolute;
    inset: 0;
    max-height: 100vh;
    max-height: 100dvh;
    z-index: 1;
    height: 100%;
    ${({height}) => height && height > 0 && `height: ${height}px;`}
    width: 100%;
    display: flex;
    flex-direction: column;
`;

const mediaWrapperStyle = css`
    ${flexAbsoluteCenter};
    flex-direction: column;
    position: relative;
    z-index: 1;
`;

const videoWrapperStyle = css`
    position: absolute;
    z-index: 2;
    inset: 0;
`;

const videoStyle = css`
    display: block;
    position: relative;
    z-index: 0;
`;

const controlsWrapperStyle = css`
    ${p(2, 2, 1, 2)};
    ${flex("center", "space-between")};
    gap: ${calculateRemSize(3)};
`;

const reelFooterStyle = css`
    flex: 1 1 auto;
    ${p(3, 2)};
    user-select: none;
    gap: ${calculateRemSize(1.5)};
    width: 100%;
`;

const actionButtonsWrapperStyle = css`
    flex: 0 0 auto;
    ${flex("center", "space-between")};
    flex-direction: row;
    gap: ${calculateRemSize(1)};
`;
